import React, { useState, useEffect, useCallback } from 'react';
import {
  Key, Copy, Check, RefreshCw, ShoppingCart, AlertCircle,
  ChevronRight, KeyRound, Eye, EyeOff, BookOpen,
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MaLicencePage() {
  const { isAuthenticated, token, loginWithDiscord } = useAuth();
  const navigate = useNavigate();

  const [licence, setLicence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  const fetchLicence = useCallback(async (silent = false) => {
    if (!token) { setLoading(false); return; }
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/user/my-licence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setLicence(data);
    } catch (e) {
      setError(e.message);
      setLicence({ has_licence: false });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) fetchLicence();
    else setLoading(false);
  }, [isAuthenticated, fetchLicence]);

  const copyKey = () => {
    if (!licence?.license_key) return;
    navigator.clipboard.writeText(licence.license_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handleResync = async () => {
    if (!token) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await fetch(`${API_URL}/api/user/licence-resync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur sync');
      setSyncMsg('Licence synchronisée — rechargement…');
      setTimeout(() => fetchLicence(true), 500);
    } catch (e) {
      setSyncMsg(`Erreur : ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  // ── Not logged in ────────────────────────────────────────────────────────
  if (!isAuthenticated) return (
    <CenteredWrap>
      <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Key className="w-6 h-6 text-cyan-400" strokeWidth={1.5} />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2 tracking-tight">Connexion requise</h2>
      <p className="text-zinc-400 text-sm mb-6 leading-relaxed">Connecte-toi avec Discord pour accéder à ta licence.</p>
      <button onClick={loginWithDiscord} data-testid="login-btn"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white text-sm font-medium rounded-lg transition-colors">
        Se connecter avec Discord
      </button>
    </CenteredWrap>
  );

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-cyan-500/20 border-t-cyan-400 rounded-full animate-spin" />
        <p className="text-zinc-500 text-xs">Chargement</p>
      </div>
    </div>
  );

  // ── No licence ───────────────────────────────────────────────────────────
  if (!licence || licence.has_licence === false) return (
    <div className="min-h-screen" data-testid="ma-licence-page">
      <AqmOptiToolLicenceCard />
      <CenteredWrap>
      <div className="w-14 h-14 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Key className="w-6 h-6 text-zinc-500" strokeWidth={1.5} />
      </div>
      <h2 className="text-xl font-semibold text-white mb-2 tracking-tight">Aucune licence active</h2>
      <p className="text-zinc-400 text-sm mb-6 leading-relaxed">
        Si tu en as déjà une, clique sur Synchroniser. Sinon, consulte nos licences.
      </p>
      <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center">
        <button onClick={handleResync} disabled={syncing}
          data-testid="resync-licence-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500 text-zinc-950 hover:bg-cyan-400 disabled:opacity-50 text-sm font-semibold rounded-lg transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synchronisation…' : 'Synchroniser'}
        </button>
        <button onClick={() => navigate('/licences')}
          data-testid="goto-licences-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-sm font-medium rounded-lg transition-colors">
          Voir les licences <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      {syncMsg && <p className="mt-5 text-xs text-zinc-400">{syncMsg}</p>}
      {error && <p className="mt-3 text-red-400 text-xs">{error}</p>}
      </CenteredWrap>
    </div>
  );

  // ── Licence data ─────────────────────────────────────────────────────────
  const isLifetime = (licence.max_uses ?? 0) >= 999;
  const usageCount = licence.usage_count ?? 0;
  const maxUses = licence.max_uses ?? 1;
  const remaining = isLifetime ? null : Math.max(0, maxUses - usageCount);
  const pct = isLifetime ? 100 : Math.min(100, (usageCount / maxUses) * 100);
  const status = licence.status || 'active';
  const isActive = status === 'active';
  const isExhausted = status === 'exhausted' || (!isLifetime && remaining === 0);
  const isRevoked = status === 'revoked';

  let statusLabel = 'Active';
  let statusColor = 'emerald';
  if (isRevoked) { statusLabel = 'Révoquée'; statusColor = 'red'; }
  else if (isExhausted) { statusLabel = 'Épuisée'; statusColor = 'orange'; }
  else if (isLifetime) { statusLabel = 'À vie'; statusColor = 'amber'; }
  else if (!isActive) { statusLabel = status; statusColor = 'zinc'; }

  const statusBg = {
    emerald: 'bg-emerald-400',
    red: 'bg-red-400',
    orange: 'bg-orange-400',
    amber: 'bg-amber-400',
    zinc: 'bg-zinc-400',
  }[statusColor];
  const statusText = {
    emerald: 'text-emerald-300',
    red: 'text-red-300',
    orange: 'text-orange-300',
    amber: 'text-amber-300',
    zinc: 'text-zinc-300',
  }[statusColor];

  return (
    <div className="min-h-screen relative" data-testid="ma-licence-page">

      {/* Section: HERO — license card */}
      <section className="max-w-3xl mx-auto px-6 pt-12 pb-4">
        {/* Breadcrumb / actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="text-zinc-400">AqmOpti</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-zinc-300">Ma licence</span>
          </div>
          <button onClick={() => fetchLicence(true)} disabled={refreshing}
            className="text-xs text-zinc-500 hover:text-cyan-400 transition-colors inline-flex items-center gap-1.5"
            data-testid="refresh-btn">
            <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.8} />
            <span>Rafraîchir</span>
          </button>
        </div>

        {/* Clean license card */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6" data-testid="license-card">
          {/* Top row */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center flex-shrink-0">
                <Key className="w-6 h-6 text-cyan-400" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-base sm:text-lg font-semibold text-white truncate">Licence AqmOpti</h1>
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-${statusColor}-500/10 border border-${statusColor}-500/25 ${statusText} text-[11px] font-semibold`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusBg} ${isActive ? 'animate-pulse' : ''}`} />
                    {statusLabel}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
                  {licence.created_at && <span>{formatDate(licence.created_at)}</span>}
                  <span className="text-zinc-600">•</span>
                  <span className={isLifetime ? 'text-amber-400/90' : 'text-cyan-400/90'}>
                    {isLifetime ? 'À vie' : `${usageCount}/${maxUses} utilisations`}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 text-sm">
              <a href="https://aqmoptimisation.fr/espace-client" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-cyan-400 transition-colors"
                data-testid="tutos-link">
                <BookOpen className="w-4 h-4" strokeWidth={1.6} />
                <span className="hidden sm:inline">Tutoriels</span>
              </a>
              <button onClick={() => navigate('/licences')}
                className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-cyan-400 transition-colors"
                data-testid="buy-uses-link">
                <ShoppingCart className="w-4 h-4" strokeWidth={1.6} />
                <span className="hidden sm:inline">Recharger</span>
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-zinc-800 my-5" />

          {/* Activation key row */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-zinc-400 flex-shrink-0">Clé d'activation :</span>
            <code className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-white font-mono text-sm tracking-wider truncate select-all"
              data-testid="license-key">
              {revealed ? (licence.license_key || '—') : '•'.repeat(Math.min(28, (licence.license_key || '').length || 16))}
            </code>
            <button onClick={() => setRevealed(v => !v)}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-cyan-400 hover:border-zinc-700 transition-colors"
              data-testid="toggle-key-btn" title={revealed ? 'Masquer' : 'Afficher'}>
              {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={copyKey}
              className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
                copied
                  ? 'bg-emerald-500 border-emerald-500 text-zinc-950'
                  : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-cyan-400 hover:border-zinc-700'
              }`}
              data-testid="copy-key-btn" title="Copier">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </section>

      {/* Section: AqmOptiTool license (premium software) */}
      <AqmOptiToolLicenceCard />

      {/* Section: STATS */}
      <section className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="text-zinc-200 text-sm font-semibold uppercase tracking-[0.15em]">Utilisations</h2>
          {!isLifetime && (
            <span className="text-xs text-zinc-500 font-mono">{usageCount} / {maxUses}</span>
          )}
        </div>

        {/* Big numbers */}
        <div className="grid grid-cols-3 gap-px bg-zinc-800/60 rounded-2xl overflow-hidden mb-5">
          <BigStat label="Utilisées" value={usageCount} sub="activations" />
          <BigStat label="Total" value={isLifetime ? '∞' : maxUses} sub={isLifetime ? 'illimité' : 'autorisées'} accent={isLifetime ? 'amber' : null} />
          <BigStat
            label="Restantes"
            value={isLifetime ? '∞' : remaining}
            sub={isLifetime ? 'à vie' : 'disponibles'}
            accent={isLifetime ? 'amber' : (remaining === 0 ? 'red' : 'cyan')}
          />
        </div>

        {/* Progress bar */}
        {!isLifetime && (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-[11px] text-zinc-500 font-medium uppercase tracking-wider">Progression</span>
              <span className="text-[11px] font-mono text-zinc-400">{Math.round(pct)}%</span>
            </div>
            <div className="relative w-full bg-zinc-900 rounded-full h-1.5 overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000"
                style={{
                  width: `${pct}%`,
                  background: pct >= 90
                    ? 'linear-gradient(90deg,#f87171,#ef4444)'
                    : pct >= 70
                      ? 'linear-gradient(90deg,#fbbf24,#f97316)'
                      : 'linear-gradient(90deg,#22d3ee,#06b6d4)',
                  boxShadow: pct >= 90
                    ? '0 0 12px rgba(239,68,68,0.45)'
                    : pct >= 70
                      ? '0 0 12px rgba(249,115,22,0.45)'
                      : '0 0 12px rgba(34,211,238,0.45)',
                }}
              />
            </div>
            {remaining === 0 && (
              <p className="text-xs text-orange-400/80 pt-1">Plus aucune utilisation — rachète un pack pour continuer.</p>
            )}
          </div>
        )}
      </section>

      {/* Section: METADATA */}
      <section className="max-w-3xl mx-auto px-6 py-8 border-t border-zinc-800/60">
        <h2 className="text-zinc-200 text-sm font-semibold uppercase tracking-[0.15em] mb-5">Informations</h2>
        <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
          <InfoRow label="Compte Discord" value={licence.discord_username || '—'} />
          <InfoRow label="ID Discord" value={licence.discord_id || '—'} mono />
          {licence.created_at && <InfoRow label="Créée le" value={formatDate(licence.created_at)} />}
          {licence.updated_at && <InfoRow label="Dernière mise à jour" value={formatDate(licence.updated_at)} />}
        </dl>
      </section>

      {/* Section: ACTIONS */}
      <section className="max-w-3xl mx-auto px-6 py-8 border-t border-zinc-800/60">
        <button onClick={() => navigate('/licences')}
          data-testid="buy-more-uses-btn"
          className="w-full group relative overflow-hidden rounded-2xl p-px hover:p-px transition-all"
          style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.4), rgba(99,102,241,0.4))' }}>
          <div className="bg-zinc-950 rounded-2xl px-6 py-5 flex items-center justify-between gap-4 group-hover:bg-zinc-900 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center group-hover:scale-105 transition-transform">
                <ShoppingCart className="w-4 h-4 text-cyan-300" strokeWidth={1.8} />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold text-sm">Ajouter des utilisations</p>
                <p className="text-zinc-500 text-xs mt-0.5">Elles s'ajoutent automatiquement à ta licence</p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-cyan-400 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </div>
        </button>
      </section>

      {/* Section: ACTIVATION GUIDE */}
      <section className="max-w-3xl mx-auto px-6 py-10 border-t border-zinc-800/60">
        <h2 className="text-zinc-200 text-sm font-semibold uppercase tracking-[0.15em] mb-6">Comment activer</h2>
        <ol className="space-y-5">
          {[
            { t: 'Rejoins le Discord', d: 'Tu trouves le lien d\'invitation en haut à droite du site.' },
            { t: 'Récupère ta clé', d: <>Dans Discord, tape la commande <code className="bg-cyan-500/10 border border-cyan-500/25 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-[11px]">/cle</code> — le bot t'envoie ta clé en privé.</> },
            { t: 'Active le logiciel', d: <>Réinstalle Windows en suivant le <a href="https://aqmoptimisation.fr/espace-client" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2 font-medium">tutoriel Windows de ton Espace Client</a>. Au démarrage, <span className="text-white">AqmOpti se lance automatiquement</span> — colle ta clé dans le champ d'activation et c'est parti.</> },
          ].map((step, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <p className="text-white text-sm font-medium mb-0.5">{step.t}</p>
                <p className="text-zinc-400 text-sm leading-relaxed">{step.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {error && (
        <div className="max-w-3xl mx-auto px-6 pb-10">
          <div className="flex items-start gap-2.5 px-4 py-3 bg-red-500/5 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="licence-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>{error}</span>
          </div>
        </div>
      )}

      <div className="pb-16" />
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function AqmOptiToolLicenceCard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [lic, setLic] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!token) { setLic(null); return; }
    fetch(`${API_URL}/api/aqmoptitool/my-license`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => setLic(d && d.has_license ? d : null))
      .catch(() => {});
  }, [token]);

  if (!lic) return null;

  const isPremium = (lic.plan || '').toLowerCase() === 'premium';
  const isExpired = lic.status === 'expired';
  const copy = () => {
    navigator.clipboard?.writeText(lic.license_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <section className="max-w-3xl mx-auto px-6 pt-6" data-testid="malicence-aqmoptitool-card">
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6">
        {/* Top row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-center flex-shrink-0">
              <KeyRound className="w-6 h-6 text-cyan-400" strokeWidth={1.6} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base sm:text-lg font-semibold text-white truncate">Licence AqmOptiTool</h2>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                  isPremium
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                    : 'bg-zinc-700/40 border-zinc-600 text-zinc-300'
                }`} data-testid="malicence-aqmoptitool-plan">
                  {isPremium ? '★ Premium' : `Plan ${lic.plan || 'Standard'}`}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${
                  isExpired
                    ? 'bg-red-500/10 border-red-500/30 text-red-300'
                    : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${isExpired ? 'bg-red-400' : 'bg-emerald-400 animate-pulse'}`} />
                  {isExpired ? 'Expirée' : 'Active'}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-500">
                <span>Logiciel premium</span>
                <span className="text-zinc-600">•</span>
                <span className="text-amber-400/90">Licence à vie</span>
              </div>
            </div>
          </div>

          {/* Action — upgrade for standard users only */}
          {!isPremium && (
            <div className="flex items-center gap-4 text-sm">
              <button onClick={() => navigate('/aqmoptitool')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-zinc-950 text-xs font-semibold transition-colors"
                data-testid="malicence-aqmoptitool-upgrade-btn">
                ★ Devenir Premium
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-zinc-800 my-5" />

        {/* Activation key row */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-zinc-400 flex-shrink-0">Clé d'activation :</span>
          <code className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-white font-mono text-sm tracking-wider truncate select-all"
            data-testid="malicence-aqmoptitool-key">
            {revealed ? (lic.license_key || '—') : '•'.repeat(Math.min(28, (lic.license_key || '').length || 16))}
          </code>
          <button onClick={() => setRevealed(v => !v)}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-cyan-400 hover:border-zinc-700 transition-colors"
            data-testid="malicence-aqmoptitool-toggle-btn" title={revealed ? 'Masquer' : 'Afficher'}>
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button onClick={copy}
            className={`flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border transition-colors ${
              copied
                ? 'bg-emerald-500 border-emerald-500 text-zinc-950'
                : 'border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-cyan-400 hover:border-zinc-700'
            }`}
            data-testid="malicence-aqmoptitool-copy-btn" title="Copier">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </section>
  );
}

const BigStat = ({ label, value, sub, accent = null }) => {
  const colorMap = {
    cyan: 'text-cyan-300',
    amber: 'text-amber-300',
    red: 'text-red-400',
  };
  return (
    <div className="bg-zinc-950 px-5 py-6">
      <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-wider mb-2">{label}</p>
      <p className={`text-4xl font-bold tracking-tight leading-none ${accent ? colorMap[accent] : 'text-white'}`}>
        {value}
      </p>
      <p className="text-[11px] text-zinc-600 mt-2">{sub}</p>
    </div>
  );
};

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-zinc-900 pb-3 last:border-0">
    <dt className="text-sm text-zinc-500 flex-shrink-0">{label}</dt>
    <dd className={`text-sm text-zinc-200 truncate min-w-0 text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
  </div>
);

const CenteredWrap = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full blur-[100px] pointer-events-none opacity-20 bg-cyan-500" />
    <div className="relative text-center max-w-sm">{children}</div>
  </div>
);

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}
